import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { resolveParamTokens } from "@/lib/paramEngine";
import {
  Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Indent, Outdent, RemoveFormatting, Baseline, Highlighter,
} from "lucide-react";

const clean = (html) => DOMPurify.sanitize(html || "", {
  ALLOWED_TAGS: ["b", "i", "u", "s", "strike", "em", "strong", "ul", "ol", "li", "br", "div", "p", "span", "font", "a", "h1", "h2", "h3", "blockquote"],
  ALLOWED_ATTR: ["style", "color", "face", "size", "href", "target"],
});

const FONTS = ["Inter", "Georgia", "Times New Roman", "Courier New", "Verdana", "Trebuchet MS", "Manrope"];
const SIZES = [{ v: "1", l: "10" }, { v: "2", l: "12" }, { v: "3", l: "14" }, { v: "4", l: "16" }, { v: "5", l: "20" }, { v: "6", l: "26" }, { v: "7", l: "34" }];
const LINE_HEIGHTS = ["1", "1.15", "1.4", "1.6", "2"];
const VALIGNS = [{ v: "top", l: "Top" }, { v: "middle", l: "Middle" }, { v: "bottom", l: "Bottom" }];

function TB({ title, onClick, active, children, testid }) {
  return (
    <button data-testid={testid} title={title} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={"h-6 w-6 flex items-center justify-center rounded hover:bg-black/10 " + (active ? "bg-black/10 text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-2)]")}>
      {children}
    </button>
  );
}

// Item 78: rich Text/Note widget — independent Title + Content sections with a floating
// formatting toolbar (execCommand-based, live, per-section styles).
export function RichTextWidget({ widget, editable = false, onConfigPatch = null, params = null }) {
  const config = widget.config || {};
  const rich = config.rich || {};
  const [editing, setEditing] = useState(null); // null | "title" | "content"
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const richRef = useRef(rich);
  richRef.current = rich;

  const save = () => {
    if (!onConfigPatch) return;
    onConfigPatch(widget.id, {
      rich: {
        ...richRef.current,
        title_html: titleRef.current ? titleRef.current.innerHTML : richRef.current.title_html,
        content_html: contentRef.current ? contentRef.current.innerHTML : richRef.current.content_html,
      },
    });
  };
  const patchSection = (sec, p) => {
    if (!onConfigPatch) return;
    onConfigPatch(widget.id, {
      rich: {
        ...richRef.current,
        title_html: titleRef.current ? titleRef.current.innerHTML : richRef.current.title_html,
        content_html: contentRef.current ? contentRef.current.innerHTML : richRef.current.content_html,
        [sec]: { ...(richRef.current[sec] || {}), ...p },
      },
    });
  };
  // Item 20: toolbar selects / colour inputs steal focus from the contentEditable, which collapses
  // the selection — remember the last range inside the edited section and restore it before every command.
  const selRef = useRef(null);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const hostOf = (sec) => (sec === "title" ? titleRef.current : contentRef.current);
  useEffect(() => {
    if (!editing) { selRef.current = null; return undefined; }
    const remember = () => {
      const s = window.getSelection();
      if (!s || s.rangeCount === 0) return;
      const r = s.getRangeAt(0);
      const host = hostOf(editingRef.current);
      if (host && host.contains(r.commonAncestorContainer)) selRef.current = r.cloneRange();
    };
    document.addEventListener("selectionchange", remember);
    return () => document.removeEventListener("selectionchange", remember);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  const restoreSel = () => {
    const host = hostOf(editingRef.current);
    if (!host) return;
    host.focus({ preventScroll: true });
    const r = selRef.current;
    if (!r) return;
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
  };
  const cmd = (name, val = null) => {
    restoreSel();
    try { document.execCommand("styleWithCSS", false, true); } catch { /* older engines */ }
    document.execCommand(name, false, val);
    if (name === "fontSize" && val) {
      // execCommand fontSize emits <font size=n>; convert to real px so exports match
      const px = { 1: 10, 2: 12, 3: 14, 4: 16, 5: 20, 6: 26, 7: 34 }[val];
      const host = editing === "title" ? titleRef.current : contentRef.current;
      host?.querySelectorAll(`font[size="${val}"]`).forEach((f) => {
        f.removeAttribute("size");
        f.style.fontSize = `${px}px`;
      });
    }
    // deliberately no save() here: saving re-renders the contentEditable and loses the caret;
    // persistence happens on blur / Done / Escape / outside click
  };
  const exitEditing = () => { save(); setEditing(null); };
  const rootRef = useRef(null);
  useEffect(() => {
    if (!editing) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) exitEditing();
    };
    const onKey = (e) => { if (e.key === "Escape") exitEditing(); };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  useEffect(() => {
    if (!editable && editing) setEditing(null);
  }, [editable, editing]);

  const secStyle = (sec, defaults) => {
    const s = rich[sec] || {};
    const st = {
      lineHeight: s.lineHeight || defaults.lineHeight,
      textAlign: s.align || defaults.align,
      color: s.color || undefined,
      padding: `${s.pad ?? 0}px 0`,
      ...(sec === "content" && s.paraSpacing ? { "--para-gap": `${s.paraSpacing}px` } : {}),
    };
    if (s.sizePx) st.fontSize = `${Number(s.sizePx)}px`;
    if (s.fontFamily) st.fontFamily = s.fontFamily;
    if (s.bold) st.fontWeight = 700; else if ("bold" in s) st.fontWeight = 400;
    if (s.italic) st.fontStyle = "italic";
    if (s.underline) st.textDecoration = "underline";
    if (s.bg) { st.background = s.bg; st.borderRadius = 8; st.padding = `${Math.max(4, Number(s.pad) || 0)}px 8px`; }
    return st;
  };
  const valign = rich.valign || "top";
  const wrapJustify = valign === "middle" ? "center" : valign === "bottom" ? "flex-end" : "flex-start";
  const isEd = (sec) => editable && editing === sec;
  const secCfg = editing ? rich[editing] || {} : {};

  return (
    <div ref={rootRef} data-testid="richtext-widget" className={"h-full w-full flex flex-col px-3 py-2 relative overflow-auto " + (editable && editing ? "pt-16" : "")}
      style={{ justifyContent: wrapJustify }}
      onMouseDown={editing ? (e) => e.stopPropagation() : undefined}>
      {editable && editing && (
        <div data-testid="richtext-toolbar" onMouseDown={(e) => e.stopPropagation()}
          className="absolute top-1 left-1 right-1 z-20 flex items-center gap-0.5 flex-nowrap overflow-x-auto rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)]/98 backdrop-blur px-1.5 py-1 shadow-lg">
          <span className="text-[9px] font-semibold uppercase tracking-wide text-[color:var(--brand-ocean)] px-1">{editing}</span>
          <select data-testid="richtext-font" className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] max-w-[92px]"
            onMouseDown={(e) => e.stopPropagation()} onChange={(e) => cmd("fontName", e.target.value)} defaultValue="">
            <option value="" disabled>Font</option>
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select data-testid="richtext-size" className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] w-[52px]"
            onMouseDown={(e) => e.stopPropagation()} onChange={(e) => cmd("fontSize", e.target.value)} defaultValue="">
            <option value="" disabled>Size</option>
            {SIZES.map((s) => <option key={s.v} value={s.v}>{s.l}px</option>)}
          </select>
          <TB testid="richtext-bold" title="Bold" onClick={() => cmd("bold")}><Bold className="w-3 h-3" /></TB>
          <TB testid="richtext-italic" title="Italic" onClick={() => cmd("italic")}><Italic className="w-3 h-3" /></TB>
          <TB testid="richtext-underline" title="Underline" onClick={() => cmd("underline")}><Underline className="w-3 h-3" /></TB>
          <TB testid="richtext-strike" title="Strikethrough" onClick={() => cmd("strikeThrough")}><Strikethrough className="w-3 h-3" /></TB>
          <label title="Text color" className="h-6 w-6 flex items-center justify-center rounded hover:bg-black/10 cursor-pointer relative overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
            <Baseline className="w-3 h-3 text-[color:var(--ink-2)]" />
            <input data-testid="richtext-color" type="color" className="absolute inset-0 w-full h-full min-w-0 appearance-none opacity-0 cursor-pointer" onChange={(e) => cmd("foreColor", e.target.value)} />
          </label>
          <label title="Highlight color" className="h-6 w-6 flex items-center justify-center rounded hover:bg-black/10 cursor-pointer relative overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
            <Highlighter className="w-3 h-3 text-[color:var(--ink-2)]" />
            <input data-testid="richtext-highlight" type="color" className="absolute inset-0 w-full h-full min-w-0 appearance-none opacity-0 cursor-pointer" onChange={(e) => cmd("hiliteColor", e.target.value)} />
          </label>
          <span className="w-px h-4 bg-[color:var(--glass-border)] mx-0.5" />
          <TB testid="richtext-align-left" title="Align left" onClick={() => cmd("justifyLeft")}><AlignLeft className="w-3 h-3" /></TB>
          <TB testid="richtext-align-center" title="Align center" onClick={() => cmd("justifyCenter")}><AlignCenter className="w-3 h-3" /></TB>
          <TB testid="richtext-align-right" title="Align right" onClick={() => cmd("justifyRight")}><AlignRight className="w-3 h-3" /></TB>
          <TB testid="richtext-align-justify" title="Justify" onClick={() => cmd("justifyFull")}><AlignJustify className="w-3 h-3" /></TB>
          {editing === "content" && (
            <>
              <span className="w-px h-4 bg-[color:var(--glass-border)] mx-0.5" />
              <TB testid="richtext-ul" title="Bulleted list" onClick={() => cmd("insertUnorderedList")}><List className="w-3 h-3" /></TB>
              <TB testid="richtext-ol" title="Numbered list" onClick={() => cmd("insertOrderedList")}><ListOrdered className="w-3 h-3" /></TB>
              <TB testid="richtext-outdent" title="Decrease indent" onClick={() => cmd("outdent")}><Outdent className="w-3 h-3" /></TB>
              <TB testid="richtext-indent" title="Increase indent" onClick={() => cmd("indent")}><Indent className="w-3 h-3" /></TB>
            </>
          )}
          <span className="w-px h-4 bg-[color:var(--glass-border)] mx-0.5" />
          <select data-testid="richtext-lineheight" title="Line spacing" className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] w-[52px]"
            onMouseDown={(e) => e.stopPropagation()} value={secCfg.lineHeight || ""} onChange={(e) => patchSection(editing, { lineHeight: e.target.value })}>
            <option value="" disabled>Line</option>
            {LINE_HEIGHTS.map((h) => <option key={h} value={h}>{h}×</option>)}
          </select>
          {editing === "content" && (
            <select data-testid="richtext-paraspacing" title="Paragraph spacing" className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] w-[56px]"
              onMouseDown={(e) => e.stopPropagation()} value={String(secCfg.paraSpacing ?? 0)} onChange={(e) => patchSection("content", { paraSpacing: Number(e.target.value) })}>
              {[0, 4, 8, 12, 16, 24].map((g) => <option key={g} value={String(g)}>{`¶ ${g}px`}</option>)}
            </select>
          )}
          <select data-testid="richtext-valign" title="Vertical alignment (whole widget)" className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] w-[62px]"
            onMouseDown={(e) => e.stopPropagation()} value={valign}
            onChange={(e) => onConfigPatch && onConfigPatch(widget.id, { rich: { ...richRef.current, valign: e.target.value } })}>
            {VALIGNS.map((v) => <option key={v.v} value={v.v}>{v.l}</option>)}
          </select>
          <TB testid="richtext-clear" title="Clear formatting" onClick={() => cmd("removeFormat")}><RemoveFormatting className="w-3 h-3" /></TB>
        </div>
      )}
      {/* Title section */}
      <div
        ref={titleRef}
        data-testid="richtext-title"
        contentEditable={isEd("title")}
        suppressContentEditableWarning
        onDoubleClick={editable ? (e) => { e.stopPropagation(); setEditing("title"); setTimeout(() => titleRef.current?.focus(), 0); } : undefined}
        onFocus={() => editable && setEditing("title")}
        onBlur={save}
        className={"font-display text-lg font-semibold text-[color:var(--ink)] outline-none min-h-[1.4em] richtext-placeholder " + (isEd("title") ? "ring-1 ring-[color:var(--brand-ocean)] rounded px-1 -mx-1" : "")}
        style={secStyle("title", { lineHeight: "1.25", align: "left" })}
        data-placeholder={editable ? "Title…" : ""}
        dangerouslySetInnerHTML={{ __html: (isEd("title") ? clean(rich.title_html) : resolveParamTokens(clean(rich.title_html), params)) || "" }}
      />
      {/* Content section */}
      <div
        ref={contentRef}
        data-testid="richtext-content"
        contentEditable={isEd("content")}
        suppressContentEditableWarning
        onDoubleClick={editable ? (e) => { e.stopPropagation(); setEditing("content"); setTimeout(() => contentRef.current?.focus(), 0); } : undefined}
        onFocus={() => editable && setEditing("content")}
        onBlur={save}
        className={"richtext-body mt-1 text-sm text-[color:var(--ink-2)] outline-none flex-0 min-h-[1.6em] richtext-placeholder " + (isEd("content") ? "ring-1 ring-[color:var(--brand-ocean)] rounded px-1 -mx-1" : "")}
        style={secStyle("content", { lineHeight: "1.5", align: "left" })}
        data-placeholder={editable ? "Double-click to edit content…" : ""}
        dangerouslySetInnerHTML={{ __html: (isEd("content") ? clean(rich.content_html) : resolveParamTokens(clean(rich.content_html), params)) || "" }}
      />
      {editable && editing && (
        <button data-testid="richtext-done" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); exitEditing(); }}
          className="absolute bottom-1 right-1 z-20 text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--brand-ocean)] text-white shadow">Done</button>
      )}
    </div>
  );
}
